import axios from 'axios';
import { configureAuthInterceptors } from './authConfig';

const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
});

// Configure auth interceptors
configureAuthInterceptors(api);

// Types for invite validation
export interface ValidateInviteResponse {
  valid: boolean;
  invite_type?: 'site' | 'org';
  email?: string;
  organization_name?: string;
  team_name?: string;
  org_role?: string;
  team_role?: string;
  expires_at?: string;
  error?: string;
}

// Types for onboarding
export interface OnboardingStatusResponse {
  needs_onboarding: boolean;
  invite_type?: 'site' | 'org';
  invite_code?: string;
  organization_name?: string;
  team_name?: string;
  // Additional context for error states
  has_expired_invite?: boolean;
  user_email?: string;
  // Resume support - last completed step name
  last_completed_step?: string;
}

// Valid onboarding step names
export type OnboardingStep = 'org_created' | 'org_joined' | 'api_key' | 'complete';

export interface SiteOnboardingRequest {
  organization_name: string;
  team_name: string;
}

export interface SiteOnboardingResponse {
  organization_id: string;
  organization_name: string;
  team_id: string;
  team_name: string;
}

export interface OrgOnboardingResponse {
  organization_id: string;
  organization_name: string;
  team_id?: string;
  team_name?: string;
  org_role: string;
  team_role?: string;
}

/**
 * Validate an invite code (public endpoint)
 */
export async function validateInvite(inviteCode: string): Promise<ValidateInviteResponse> {
  const response = await api.get<ValidateInviteResponse>(`/invites/validate/${inviteCode}`);
  return response.data;
}

/**
 * Get onboarding status for current user
 */
export async function getOnboardingStatus(): Promise<OnboardingStatusResponse> {
  const response = await api.get<OnboardingStatusResponse>('/onboarding/status');
  return response.data;
}

/**
 * Complete site invite onboarding (create org and team)
 */
export async function completeSiteOnboarding(data: SiteOnboardingRequest): Promise<SiteOnboardingResponse> {
  const response = await api.post<SiteOnboardingResponse>('/onboarding/site', data);
  return response.data;
}

/**
 * Accept org invite (join existing org/team)
 */
export async function acceptOrgInvite(inviteCode: string): Promise<OrgOnboardingResponse> {
  const response = await api.post<OrgOnboardingResponse>(`/onboarding/org/${inviteCode}`);
  return response.data;
}

/**
 * Update onboarding progress step
 */
export async function updateOnboardingStep(step: OnboardingStep): Promise<{ success: boolean; step: string }> {
  const response = await api.post('/onboarding/step', { step });
  return response.data;
}

/**
 * Get login URL with invite code
 */
export function getLoginUrlWithInvite(provider: string, inviteCode: string): string {
  return `/api/auth/login/${provider}?invite_code=${encodeURIComponent(inviteCode)}`;
}
